import { Children, CSSProperties, isValidElement, ReactElement, ReactNode, useState } from 'react'
import { Tabs } from '@common/ui'

type PersistentTabProps<T extends string = string> = {
  label: string
  value: T
  children: ReactNode
}

export const PersistentTab = <T extends string>(_props: PersistentTabProps<T>): ReactElement => {
  return null as unknown as ReactElement
}

type PersistentTabsProps<T extends string> = {
  defaultValue: T
  children: ReactNode
  style?: CSSProperties
}

const PersistentTabs = <T extends string>({ defaultValue, children, style }: PersistentTabsProps<T>) => {
  const [value, setValue] = useState<T>(defaultValue)
  const [mountedTabs, setMountedTabs] = useState<Set<T>>(() => new Set([defaultValue]))

  const tabs: PersistentTabProps<T>[] = []
  Children.forEach(children, (child) => {
    if (isValidElement<PersistentTabProps<T>>(child) && child.type === PersistentTab) {
      tabs.push(child.props)
    }
  })

  const handleChange = (newValue: string | null) => {
    if (newValue === null) return
    const typedValue = newValue as T
    setMountedTabs((prev) => {
      if (prev.has(typedValue)) return prev
      return new Set(prev).add(typedValue)
    })
    setValue(typedValue)
  }

  return (
    <>
      <Tabs value={value} onChange={handleChange} style={style}>
        <Tabs.List>
          {tabs.map((tab) => (
            <Tabs.Tab key={tab.value} value={tab.value}>
              {tab.label}
            </Tabs.Tab>
          ))}
        </Tabs.List>
      </Tabs>
      {tabs.map(
        (tab) =>
          mountedTabs.has(tab.value) && (
            <div key={tab.value} style={{ display: value === tab.value ? 'contents' : 'none' }}>
              {tab.children}
            </div>
          )
      )}
    </>
  )
}

export default PersistentTabs
